import React, { useState, useEffect } from "react";
import { getCandidateCV } from "@/api/candidateCV.jsx";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { toast } from "sonner";
import { Download, FileText, Eye, ExternalLink } from "lucide-react";

export default function CandidateCV({ candidateId, candidateName }) {
    const [cvData, setCvData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCV();
    }, [candidateId]);

    const loadCV = async () => {
        setLoading(true);
        try {
            const data = await getCandidateCV(candidateId);
            setCvData(data);
        } catch (error) {
            console.error("Error loading CV:", error);
            toast.error("Failed to load CV");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (cvData?.documentUrl) {
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = cvData.documentUrl;
            link.download = `${candidateName}_CV.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleView = () => {
        if (cvData?.documentUrl) {
            window.open(cvData.documentUrl, '_blank');
        }
    };

    if (loading) {
        return <div className="text-center py-4">Loading CV...</div>;
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Curriculum Vitae</CardTitle>
                </CardHeader>
                <CardContent>
                    {cvData?.fileExists ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 p-4 border rounded-lg bg-gray-50">
                                <FileText className="h-8 w-8 text-blue-600" />
                                <div className="flex-1">
                                    <h3 className="font-medium text-lg">CV Document</h3>
                                    <p className="text-sm text-gray-600">
                                        CV for {candidateName}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={handleView}
                                        className="flex items-center gap-2"
                                        variant="outline"
                                    >
                                        <Eye className="h-4 w-4" />
                                        View
                                    </Button>
                                    <Button
                                        onClick={handleDownload}
                                        className="flex items-center gap-2"
                                    >
                                        <Download className="h-4 w-4" />
                                        Download
                                    </Button>
                                </div>
                            </div>
                            
                            {/* CV Preview iframe */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Eye className="h-5 w-5" />
                                        CV Preview
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={handleView}
                                            className="ml-auto flex items-center gap-1"
                                        >
                                            <ExternalLink className="h-3 w-3" />
                                            Open in new tab
                                        </Button>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="w-full h-96 border rounded-lg overflow-hidden">
                                        <iframe
                                            src={cvData.documentUrl}
                                            className="w-full h-full"
                                            title={`CV for ${candidateName}`}
                                            frameBorder="0"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        If the CV doesn't display correctly, try using the "View" or "Download" buttons above.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                            <h3 className="text-lg font-medium mb-2">No CV Uploaded</h3>
                            <p className="text-sm">
                                {candidateName} hasn't uploaded a CV yet.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}